import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ToothMark } from "@/components/Logo";
import { SERVICES } from "@/data/site";

export const TeamMemberDialog = ({ member, onClose }) => {
    const open = Boolean(member);
    const m = member;
    const galleryRef = useRef(null);
    const scrollBy = (dir) => {
        const el = galleryRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                data-testid="team-dialog"
                className="max-h-[92vh] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto rounded-3xl border-line p-0 sm:rounded-3xl"
            >
                {m && (
                    <div className="grid grid-cols-1 md:grid-cols-12">
                        <div className="relative md:col-span-5">
                            {(m.photos?.length ? m.photos : m.photo ? [m.photo] : []).length > 0 ? (
                                <div
                                    ref={galleryRef}
                                    data-testid="team-dialog-gallery"
                                    className="flex h-64 snap-x snap-mandatory overflow-x-auto scroll-smooth md:h-full [&::-webkit-scrollbar]:hidden"
                                    style={{ scrollbarWidth: "none" }}
                                >
                                    {(m.photos?.length ? m.photos : [m.photo]).map((src, i) => (
                                        <img
                                            key={i}
                                            src={src}
                                            alt={`${m.name} — foto ${i + 1}`}
                                            data-testid={`team-dialog-photo-${i}`}
                                            className="h-full w-full shrink-0 snap-center object-cover"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex h-64 flex-col items-center justify-center gap-4 bg-ice md:h-full">
                                    <ToothMark className="h-10 w-10 opacity-60" />
                                    <p className="text-[0.68rem] font-light uppercase tracking-[0.2em] text-muted">
                                        Foto in arrivo
                                    </p>
                                </div>
                            )}
                            {m.photos?.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => scrollBy(-1)}
                                        aria-label="Foto precedente"
                                        data-testid="team-dialog-prev"
                                        className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-ink shadow-md backdrop-blur transition hover:bg-white"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => scrollBy(1)}
                                        aria-label="Foto successiva"
                                        data-testid="team-dialog-next"
                                        className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-ink shadow-md backdrop-blur transition hover:bg-white"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="p-7 sm:p-10 md:col-span-7">
                            <p className="text-[0.68rem] font-medium uppercase tracking-[0.24em] text-petrol">
                                {m.role}
                            </p>
                            <DialogTitle
                                data-testid="team-dialog-name"
                                className="mt-2 font-serif text-2xl font-normal leading-tight text-ink sm:text-3xl"
                            >
                                {m.name}
                            </DialogTitle>
                            <DialogDescription className="sr-only">{m.bio}</DialogDescription>
                            <div className="mt-5 space-y-4 text-sm font-light leading-relaxed tracking-wide text-body sm:text-base">
                                {m.details.map((p) => <p key={p}>{p}</p>)}
                            </div>

                            <p className="mt-8 text-[0.68rem] font-medium uppercase tracking-[0.24em] text-petrol">
                                Di cosa si occupa
                            </p>
                            <ul data-testid="team-dialog-focus" className="mt-4 grid gap-2.5 sm:grid-cols-2">
                                {m.focus.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-ink">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                                        <span className="font-light">{f}</span>
                                    </li>
                                ))}
                            </ul>

                            {m.services?.length > 0 && (
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {m.services.map((id) => {
                                        const s = SERVICES.find((x) => x.id === id);
                                        return s ? (
                                            <Link
                                                key={id}
                                                to={`/servizi#${id}`}
                                                onClick={onClose}
                                                data-testid={`team-dialog-service-${id}`}
                                                className="inline-flex items-center gap-1.5 rounded-full border border-petrol/30 bg-white px-4 py-2 text-xs font-normal text-petrol transition-colors duration-300 hover:bg-petrol hover:text-white"
                                            >
                                                {s.name}
                                                <ArrowUpRight className="h-3.5 w-3.5" />
                                            </Link>
                                        ) : null;
                                    })}
                                </div>
                            )}

                            <Link
                                to="/contatti"
                                onClick={onClose}
                                data-testid="team-dialog-cta"
                                className="mt-8 inline-flex items-center gap-3 rounded-full bg-petrol px-7 py-3.5 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Prenota una visita
                            </Link>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
